import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { toast } from '../../../../../ui/toast';
import { Button, Dialog } from '../../../../../ui';
import {
  IUserDisplay,
  PermissionType,
  createAccessControl,
  deleteAccessControl,
  fetchAccessControlList,
} from '../../../../../utils/api_service/access_control';
import { getEmployeeList } from '../../../../../utils/api_service/employees';
import { EmployeeUser } from '../../../../../utils/types/admin_types';
import { SelectOptions } from '../../../../../utils/types/react_select_types';
import { DIALOG_TYPE } from '../../../Constants/DialogTypes';
import { SelectedUserPermissions } from '../JobRequisition/Components/SelectedUserPermissions';
import { ContributorSelectWithDisplay } from './Components/ContributorSelectWithDisplay';

// Utility function for debouncing
function debounce<T extends (...args: any[]) => any>(
  func: T,
  wait: number,
): T & { cancel: () => void } {
  let timeout: NodeJS.Timeout | null = null;

  const debounced = ((...args: Parameters<T>) => {
    if (timeout) clearTimeout(timeout);
    timeout = setTimeout(() => func(...args), wait);
  }) as T & { cancel: () => void };

  debounced.cancel = () => {
    if (timeout) {
      clearTimeout(timeout);
      timeout = null;
    }
  };

  return debounced;
}

// Utility functions
function sanitizePermissions(perms: string[] | undefined): PermissionType[] {
  if (!perms) return [];
  return perms.filter(
    (p): p is PermissionType => p === 'read' || p === 'write' || p === 'delete',
  );
}

function mapACLToUserDisplay(aclEntry: any): IUserDisplay {
  return {
    firstName: aclEntry.orgUser.user.firstName,
    lastName: aclEntry.orgUser.user.lastName,
    email: aclEntry.orgUser.user.email,
    id: aclEntry.orgUser.user.id,
    orgUserId: aclEntry.orgUser.id,
    permissions: sanitizePermissions(aclEntry.permissions),
  };
}

function mapEmployeeToUserDisplay(
  employee: EmployeeUser,
  permissions: PermissionType[] = [],
): IUserDisplay {
  return {
    firstName: employee.user.firstName,
    lastName: employee.user.lastName,
    email: employee.user.email,
    id: employee.user.id,
    orgUserId: employee.id,
    permissions,
  };
}

export interface IPermissionsProps {
  resourceId: string;
  resourceType: string;
  defaultHiringManager: IUserDisplay[];
  defaultInternalRecruiter: IUserDisplay[];
  employees?: EmployeeUser[]; // Made optional since we'll fetch from API
}

export const Permissions: React.FC<IPermissionsProps> = ({
  resourceId,
  resourceType,
  defaultHiringManager = [],
  defaultInternalRecruiter = [],
  employees = [],
}) => {
  // Main state
  const [usersWithAccess, setUsersWithAccess] = useState<IUserDisplay[]>([]);
  const [assignableHMs, setAssignableHMs] = useState<IUserDisplay[]>([]);
  const [assignableIRs, setAssignableIRs] = useState<IUserDisplay[]>([]);
  const [allPermissions, setAllPermissions] = useState<
    Record<string, PermissionType[]>
  >({});

  // Dialog state
  const [confirmDialogState, setConfirmDialogState] = useState(false);
  const [userToRemove, setUserToRemove] = useState<IUserDisplay | null>(null);

  // Loading states
  const [loading, setLoading] = useState(false);
  const [isLoadingEmployees, setIsLoadingEmployees] = useState(false);

  // Employee/Contributor state
  const [searchTerm, setSearchTerm] = useState('');
  const [employeeList, setEmployeeList] = useState<EmployeeUser[]>(employees);
  const [selectedContributorOptions, setSelectedContributorOptions] = useState<
    SelectOptions[]
  >([]);
  const [selectedContributors, setSelectedContributors] = useState<
    IUserDisplay[]
  >([]);
  const [hasShownError, setHasShownError] = useState(false);

  // Memoized values
  const usersWithAccessIds = useMemo(
    () => new Set(usersWithAccess.map((u) => u.id)),
    [usersWithAccess],
  );

  const availableContributors = useMemo(
    () => employeeList.filter((emp) => !usersWithAccessIds.has(emp.user.id)),
    [employeeList, usersWithAccessIds],
  );

  const contributorOptions = useMemo(
    () =>
      availableContributors.map((employee) => ({
        value: employee.id,
        label: `${employee.user.firstName} ${employee.user.lastName}`,
      })),
    [availableContributors],
  );

  // Employee fetching with debounce
  const fetchEmployees = useCallback(
    async (search: string = '') => {
      setIsLoadingEmployees(true);
      setHasShownError(false);

      try {
        let allEmployees: EmployeeUser[] = [];
        let currentPage = 1;
        let hasMore = true;

        // Fetch all pages to get complete employee list
        while (hasMore) {
          const response = await getEmployeeList(search, currentPage, 50);

          if (!response.ok) {
            const errorData = await response.json();
            if (!hasShownError) {
              toast.error(errorData.error || 'Failed to fetch employees', {
                position: 'bottom-center',
              });
              setHasShownError(true);
            }
            throw new Error(errorData.error || 'Failed to fetch employees');
          }

          const data = await response.json();
          allEmployees = [...allEmployees, ...data.employee];
          hasMore = currentPage < data.pages;
          currentPage++;
        }

        setEmployeeList(allEmployees);
      } catch (error) {
        if (!hasShownError) {
          toast.error('Failed to fetch employees', {
            position: 'bottom-center',
          });
          setHasShownError(true);
        }
        setEmployeeList([]);
      } finally {
        setIsLoadingEmployees(false);
      }
    },
    [hasShownError],
  );

  const debouncedFetchEmployees = useMemo(
    () => debounce(fetchEmployees, 300),
    [fetchEmployees],
  );

  // Effects
  useEffect(() => {
    // Initial employee fetch
    fetchEmployees('');

    return () => {
      debouncedFetchEmployees.cancel();
    };
  }, [fetchEmployees, debouncedFetchEmployees]);

  useEffect(() => {
    // Debounced search when search term changes
    if (searchTerm !== '') {
      debouncedFetchEmployees(searchTerm);
    } else {
      fetchEmployees('');
    }
  }, [searchTerm, debouncedFetchEmployees, fetchEmployees]);

  // Access control list management
  const refetchAccessControlList = useCallback(() => {
    if (!resourceId || !resourceType) return;

    setLoading(true);
    fetchAccessControlList(resourceId, resourceType)
      .then((aclList) => {
        const aclUserDisplays = aclList.map(mapACLToUserDisplay);
        setUsersWithAccess(aclUserDisplays);

        const aclUserIds = new Set(aclUserDisplays.map((u) => u.id));
        setAssignableHMs(
          defaultHiringManager.filter((u) => !aclUserIds.has(u.id)),
        );
        setAssignableIRs(
          defaultInternalRecruiter.filter((u) => !aclUserIds.has(u.id)),
        );

        const perms: Record<string, PermissionType[]> = {};
        aclUserDisplays.forEach((u) => {
          perms[u.id] = u.permissions;
        });
        setAllPermissions(perms);
      })
      .catch((error) => {
        console.error('Error fetching access control list:', error);

      })
      .finally(() => setLoading(false));
  }, [
    resourceId,
    resourceType,
    defaultHiringManager,
    defaultInternalRecruiter,
  ]);

  useEffect(() => {
    refetchAccessControlList();
  }, [refetchAccessControlList]);

  // Permission management
  const handlePermissionChange = useCallback(
    (userId: string, perms: PermissionType[]) => {
      setAllPermissions((prev) => ({ ...prev, [userId]: perms }));
    },
    [],
  );

  // Contributor management
  const mapOptionToUserDisplay = useCallback(
    (option: SelectOptions): IUserDisplay | undefined => {
      const employee = employeeList.find((emp) => emp.id === option.value);
      if (!employee) return undefined;

      return mapEmployeeToUserDisplay(
        employee,
        allPermissions[employee.user.id] || [],
      );
    },
    [employeeList, allPermissions],
  );

  const handleContributorsChange = useCallback(
    (options: SelectOptions[]) => {
      // Filter out contributors who may have gained access since selection
      const filteredOptions = options.filter((opt) => {
        const employee = employeeList.find((emp) => emp.id === opt.value);
        return employee && !usersWithAccessIds.has(employee.user.id);
      });

      setSelectedContributorOptions(filteredOptions);

      const users = filteredOptions
        .map(mapOptionToUserDisplay)
        .filter((user): user is IUserDisplay => user !== undefined);

      setSelectedContributors(users);

      // Initialize permissions for new contributors
      users.forEach((user) => {
        setAllPermissions((prev) => ({
          ...prev,
          [user.id]: prev[user.id] || [],
        }));
      });
    },
    [employeeList, usersWithAccessIds, mapOptionToUserDisplay],
  );

  // Clean up selected contributors when they gain access
  useEffect(() => {
    if (selectedContributorOptions.length > 0) {
      const stillAvailable = selectedContributorOptions.filter((opt) => {
        const employee = employeeList.find((emp) => emp.id === opt.value);
        return employee && !usersWithAccessIds.has(employee.user.id);
      });

      if (stillAvailable.length !== selectedContributorOptions.length) {
        setSelectedContributorOptions(stillAvailable);
        const users = stillAvailable
          .map(mapOptionToUserDisplay)
          .filter((user): user is IUserDisplay => user !== undefined);
        setSelectedContributors(users);
      }
    }
  }, [
    usersWithAccessIds,
    employeeList,
    selectedContributorOptions,
    mapOptionToUserDisplay,
  ]);

  // Delete operations
  const handleDelete = useCallback(
    async (orgUserId: string) => {
      try {
        await deleteAccessControl({
          orgUserId,
          resourceId,
          resourceType,
        });
        toast.success('Access removed!', { position: 'bottom-center' });
        refetchAccessControlList();
      } catch (error) {
        toast.error('Failed to remove access.', { position: 'bottom-center' });
      }
    },
    [resourceId, resourceType, refetchAccessControlList],
  );

  const handleRequestDelete = useCallback((user: IUserDisplay) => {
    setUserToRemove(user);
    setConfirmDialogState(true);
  }, []);

  const handleConfirmRemove = useCallback(async () => {
    if (userToRemove?.orgUserId) {
      await handleDelete(userToRemove.orgUserId);
      setUserToRemove(null);
      setConfirmDialogState(false);
    }
  }, [userToRemove, handleDelete]);

  const handleCancelRemove = useCallback(() => {
    setUserToRemove(null);
    setConfirmDialogState(false);
  }, []);

  // Save permissions
  const handleSavePermissions = useCallback(async () => {
    const entries = [
      ...assignableHMs,
      ...assignableIRs,
      ...selectedContributors,
    ]
      .filter(
        (u) =>
          u.orgUserId && allPermissions[u.id] && allPermissions[u.id].length > 0,
      )
      .map((u) => ({
        orgUserId: u.orgUserId as string,
        permissions: allPermissions[u.id].map(String),
      }));

    if (entries.length === 0) {
      toast.info('No permissions selected.', { position: 'bottom-center' });
      return;
    }

    const payload = { resourceId, resourceType, entries };

    try {
      await createAccessControl(payload);
      toast.success('Permissions saved!', { position: 'bottom-center' });
      refetchAccessControlList();

      // Clear selected contributors after successful save
      setSelectedContributorOptions([]);
      setSelectedContributors([]);
    } catch (error) {
      toast.error('Failed to save permissions.', { position: 'bottom-center' });
    }
  }, [
    assignableHMs,
    assignableIRs,
    selectedContributors,
    allPermissions,
    resourceId,
    resourceType,
    refetchAccessControlList,
  ]);

  // Filter users by permission level
  const creatorsWithAccess = useMemo(
    () => usersWithAccess.filter((user) => user.permissions.includes('delete')),
    [usersWithAccess],
  );

  const otherUsersWithAccess = useMemo(
    () =>
      usersWithAccess.filter((user) => !user.permissions.includes('delete')),
    [usersWithAccess],
  );

  if (loading) {
    return (
      <div className='p-3 sm:p-6 w-full'>
        <h2 className='mb-4 font-bold'>Permissions</h2>
        <div className='flex justify-center items-center h-32'>
          <div>Loading permissions...</div>
        </div>
      </div>
    );
  }

  return (
    <div className='w-full min-w-0 p-2 sm:p-4 lg:p-6'>
      <h2 className='mb-4 text-lg font-bold text-slate-950'>Permissions</h2>

      <div className='flex flex-col gap-4 xl:flex-row xl:gap-6'>
        { /* Left Column: Permissions List */ }
        <div className='min-w-0 flex-1'>
          { /* Creator Access */ }
          { creatorsWithAccess.length > 0 && (
            <div className='bg-white rounded-lg shadow-sm p-3 mb-4 border border-gray-100 sm:p-4'>
              <SelectedUserPermissions
                  title='Users with Creator Access'
                  users={creatorsWithAccess}
                  onPermissionChange={handlePermissionChange}
                  readOnly
              />
            </div>
          ) }

          { /* Other Users with Access */ }
          { otherUsersWithAccess.length > 0 && (
            <div className='bg-white rounded-lg shadow-sm p-3 mb-4 border border-gray-100 sm:p-4'>
              <SelectedUserPermissions
                  title='Users with Access'
                  users={otherUsersWithAccess}
                  onPermissionChange={handlePermissionChange}
                  readOnly
                  onDelete={handleRequestDelete}
              />
            </div>
          ) }

          { /* Assignable Hiring Managers */ }
          { assignableHMs.length > 0 && (
            <div className='mb-6'>
              <div className='border-t border-dashed border-gray-200 my-4' />
              <div className='bg-white rounded-lg shadow-sm p-3 border border-gray-100 sm:p-4'>
                <SelectedUserPermissions
                    title='Hiring Manager(s) – Assign Permissions'
                    users={assignableHMs}
                    onPermissionChange={handlePermissionChange}
                />
              </div>
            </div>
          ) }

          { /* Assignable Internal Recruiters */ }
          { assignableIRs.length > 0 && (
            <div className='mb-6'>
              <div className='border-t border-dashed border-gray-200 my-4' />
              <div className='bg-white rounded-lg shadow-sm p-3 border border-gray-100 sm:p-4'>
                <SelectedUserPermissions
                    title='Internal Recruiter(s) – Assign Permissions'
                    users={assignableIRs}
                    onPermissionChange={handlePermissionChange}
                />
              </div>
            </div>
          ) }

          { /* Selected Contributors */ }
          { selectedContributors.length > 0 && (
            <div>
              <div className='border-t border-dashed border-gray-200 my-4' />
              <div className='bg-white rounded-lg shadow-sm p-3 border border-gray-100 sm:p-4'>
                <SelectedUserPermissions
                    title='Contributors'
                    users={selectedContributors}
                    onPermissionChange={handlePermissionChange}
                />
              </div>
            </div>
          ) }
        </div>

        { /* Right Column: Contributor Select */ }
        <div className='w-full flex flex-col gap-4 xl:w-[300px] xl:shrink-0'>
          <div className='bg-white rounded-lg shadow-sm p-3 border border-gray-100 sm:p-4 xl:sticky xl:top-24'>
            <h3 className='text-base font-semibold mb-3 text-gray-700'>
              Add Contributors
            </h3>

            <ContributorSelectWithDisplay
                employees={availableContributors}
                editable={true}
                value={selectedContributorOptions}
                onChange={handleContributorsChange}
              // isLoading={isLoadingEmployees}
            />

            <div className='mt-3 text-xs text-gray-400'>
              Contributors added here will appear in the panel on the left.
              Users already with access will not be shown.
            </div>
          </div>
        </div>
      </div>

      { /* Action Buttons */ }
      <div className='flex flex-col items-stretch justify-end gap-2 mt-4 sm:flex-row sm:items-center'>
        <Button
            label='Cancel'
            onClick={() => {
              setSelectedContributorOptions([]);
              setSelectedContributors([]);
            }}
            variant='secondary'
            className='w-full sm:w-auto'
        />
        <Button
            label='Save Permissions'
            onClick={handleSavePermissions}
            variant='primary'
            disabled={loading}
            className='w-full sm:w-auto'
        />
      </div>

      { /* Confirmation Dialog */ }
      <Dialog
          title='Remove Permission'
          handleApply={handleConfirmRemove}
          confirmButtonLable='Yes, Remove'
          isOpen={confirmDialogState}
          onClose={handleCancelRemove}
          cancelButtonLable='No, Cancel'
          dialogType={DIALOG_TYPE.WARNING}
          justify='end'
      >
        <p className='text-primary-textGray text-sm'>
          { userToRemove && (
            <>
              Are you sure you want to remove <br />
              <b>
                { userToRemove.firstName } { userToRemove.lastName } (
                { userToRemove.email })
              </b>{ ' ' }
              <br />
              from this resource?
              <br />
              <br />
              <ul className='list-disc pl-5'>
                <li>This action cannot be undone.</li>
                <li>Any permissions granted to this user will be lost.</li>
              </ul>
            </>
          ) }
        </p>
      </Dialog>
    </div>
  );
};
